import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from './ui/button';

/**
 * BackButton — always-available escape hatch for headerless pages
 * (signup, accept-invite, coming-soon, etc.).
 *
 * Behaviour:
 *   • If the user arrived from another route in this SPA session,
 *     go back one step (browser history).
 *   • If they landed here directly (bookmark, external link, direct URL),
 *     fall back to `/` so they're never stuck without an exit.
 *
 * Positioned absolutely at the top-left by default so it never eats
 * layout space in the card/form beneath it. Callers can pass a `className`
 * to override placement (e.g. inline within a header).
 */
export const BackButton = ({
  className = 'absolute top-4 left-4 sm:top-6 sm:left-6 z-10',
  label = 'Back',
  fallbackHref = '/',
  testId = 'back-btn',
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleClick = () => {
    // React-Router v6 tracks history length via window.history.length. If we
    // came from another SPA route, key !== 'default'; that is the reliable
    // signal that -1 will land somewhere useful.
    if (location.key && location.key !== 'default') {
      navigate(-1);
    } else {
      navigate(fallbackHref, { replace: true });
    }
  };

  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={handleClick}
      className={`${className} gap-1.5 text-text-primary hover:bg-gray-100`}
      data-testid={testId}
      aria-label={label}
    >
      <ArrowLeft className="h-4 w-4" />
      <span className="hidden sm:inline">{label}</span>
    </Button>
  );
};

export default BackButton;
